import React from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  Stack,
  Typography
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
// import RuleIcon from '@mui/icons-material/Rule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';

const SemanticMetrics = () => {
  return (
    <PageContainer
      title="Métricas & Business Rules"
      description="Gobierna indicadores críticos, valida reglas de negocio y distribuye versiones certificadas a toda la organización."
      actions={[
        <Button key="rule" variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Crear regla
        </Button>
      ]}
    >
      <StatGrid
        items={[
          { label: 'Métricas oficiales', value: '58', trend: '+7 nuevas definiciones' },
          { label: 'Reglas activas', value: '142', trend: '+12 reglas testeadas' },
          { label: 'Alertas críticas', value: '2', trend: '+1 requiere análisis' },
          { label: 'Cobertura dashboards', value: '96%', trend: '+4% adopción' }
        ]}
      />

      <Section
        title="Principales métricas"
        subtitle="Cada métrica cuenta con owner, periodicidad y regla de cálculo documentada"
      >
        <Grid container spacing={3}>
          {[
            {
              name: 'Revenue recurrente neto',
              owner: 'Equipo de Crecimiento',
              frequency: 'Actualización diaria',
              trend: '+12% QTD',
              icon: <TrendingUpIcon sx={{ color: 'var(--wb-success)' }} />
            },
            {
              name: 'Customer Health Score',
              owner: 'Customer Success',
              frequency: 'Actualización semanal',
              trend: '85 puntos',
              icon: <DataUsageIcon sx={{ color: 'var(--wb-accent)' }} />
            }
          ].map((metric) => (
            <Grid item xs={12} md={6} key={metric.name}>
              <Box
                sx={{
                  backgroundColor: 'var(--wb-bg-surface)',
                  borderRadius: 2,
                  border: '1px solid var(--wb-border-10)',
                  p: 3,
                  height: '100%'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {metric.icon}
                  <Box>
                    <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{metric.name}</Typography>
                    <Typography sx={{ color: 'var(--wb-muted-75)', fontSize: '0.85rem' }}>
                      Owner: {metric.owner}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={metric.frequency}
                  size="small"
                  sx={{
                    backgroundColor: 'var(--wb-x-bg-0ea5e91f, rgba(14, 165, 233, 0.12))',
                    color: 'var(--wb-accent)',
                    width: 'fit-content'
                  }}
                />
                <Typography sx={{ color: 'var(--wb-muted-75)', mt: 2 }}>{metric.trend}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section
        title="Reglas recientes"
        subtitle="Auditoría centralizada de reglas de negocio y cálculos"
      >
        <List>
          {[
            {
              title: 'Validar revenue con notas de crédito',
              detail: 'Compara contra ledger contable y automatiza ajuste por moneda'
            },
            {
              title: 'Score de retención',
              detail: 'Evalúa churn, adopción de features y uso de soporte'
            },
            {
              title: 'SLA de respuesta a tickets',
              detail: 'Calcula cumplimiento por severidad y canal'
            }
          ].map((rule) => (
            <ListItem key={rule.title} sx={{ px: 0 }}>
              <Stack spacing={1.2}>
                <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{rule.title}</Typography>
                <Typography sx={{ color: 'var(--wb-muted-76)' }}>{rule.detail}</Typography>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Section>

      <Section
        title="Alertas activas"
        subtitle="Acciona rápidamente cuando una regla crítica detecta desviaciones"
      >
        <Stack spacing={2}>
          {[
            {
              title: 'Variación de margen > 5%',
              detail: 'Detección automática en canal ecommerce · impacto alto',
              level: 'Alta'
            },
            {
              title: 'Anomalía en conversión trial → pago',
              detail: 'Caída de 9ptos vs. baseline · revisando funnel onboarding',
              level: 'Media'
            }
          ].map((alert) => (
            <Box
              key={alert.title}
              sx={{
                backgroundColor: 'var(--wb-bg-surface)',
                borderRadius: 2,
                border: '1px solid var(--wb-border-10)',
                p: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{alert.title}</Typography>
                <Typography sx={{ color: 'var(--wb-muted-75)', mt: 0.5 }}>{alert.detail}</Typography>
              </Box>
              <Chip
                icon={<WarningAmberIcon sx={{ color: alert.level === 'Alta' ? 'var(--wb-danger)' : 'var(--wb-x-text-facc15, #facc15)' }} />}
                label={alert.level}
                sx={{
                  backgroundColor: alert.level === 'Alta' ? 'var(--wb-x-bg-f8717124, rgba(248, 113, 113, 0.14))' : 'var(--wb-x-bg-facc1524, rgba(250, 204, 21, 0.14))',
                  color: alert.level === 'Alta' ? 'var(--wb-danger)' : 'var(--wb-x-text-facc15, #facc15)',
                  '& .MuiChip-icon': {
                    color: alert.level === 'Alta' ? 'var(--wb-danger)' : 'var(--wb-x-text-facc15, #facc15)'
                  }
                }}
              />
            </Box>
          ))}
        </Stack>
      </Section>
    </PageContainer>
  );
};

export default SemanticMetrics;
